import React, { useMemo, useRef } from 'react';
import { round } from 'lodash';
import { Box, Card, Typography } from '@mui/material';
import { mapDegreesCardinal } from '@surfline/web-common';

import ObservedBadge from 'components/ObservedBadge';
import CurrentWindMap from 'components/CurrentWindMap';
import type { GeoLocation } from 'types/locationView';
import type { Units } from 'types/units';
import type { WindTooltipData } from 'types/graphs';

import styles from './WindTooltip.module.scss';

interface Props {
  activeWind: WindTooltipData;
  graphWidth: number;
  isMobile: boolean;
  location: GeoLocation;
  mapZoom?: number;
  units: Units;
}

export const WIND_CARD_PADDING = 20;

const WindTooltip: React.FC<Props> = ({
  activeWind,
  graphWidth,
  isMobile,
  location,
  mapZoom,
  units,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const wind = activeWind?.data;
  const formattedDirection = wind?.directionType?.toLowerCase() ?? null;
  const directionAbbr = wind?.direction ? mapDegreesCardinal(wind.direction) : null;
  const unitLowercase = units?.windSpeed?.toLowerCase() ?? null;
  const isWindLiveCurrently = activeWind?.data?.isLiveCurrently ?? false;

  const positionStyles = useMemo(() => {
    const cardWidth = cardRef?.current?.offsetWidth || 0;
    let leftPosition = 0;
    if (activeWind?.leftPosition >= 0) {
      leftPosition = Math.min(
        isMobile ? activeWind.leftDayPosition : activeWind.leftPosition - WIND_CARD_PADDING,
        graphWidth - cardWidth,
      );
    }
    return {
      left: Math.max(leftPosition, 0),
    };
  }, [activeWind?.leftDayPosition, activeWind?.leftPosition, graphWidth, isMobile]);

  if (!wind) return null;

  return (
    <Box className={styles.windTooltipContainer}>
      <Card
        className={styles.tooltipCard}
        data-testid="wind-tooltip"
        ref={cardRef}
        style={positionStyles}
      >
        <Box className={styles.contentContainer}>
          <Box className={styles.sectionHeader}>
            <Typography component="h3" variant="caption2" className={styles.sectionLabel}>
              Wind
            </Typography>
          </Box>
          <Box>
            <Box className={styles.windRow}>
              <Box className={styles.windSpeedContainer}>
                <Typography component="span" variant="title3" className={styles.windSpeed}>
                  {round(wind.speed || 0)}
                  <Typography component="span" variant="callout1" className={styles.speedUnit}>
                    {unitLowercase}
                  </Typography>
                </Typography>{' '}
                <Typography
                  component="span"
                  variant="callout1"
                  className={styles.windDirectionText}
                >
                  {directionAbbr}
                </Typography>
              </Box>
              {isWindLiveCurrently && (
                <div className={styles.observedBadge}>
                  <ObservedBadge />
                </div>
              )}
            </Box>
            <Typography className={styles.windGustInfo} variant="caption1">
              {wind.gust ? `${round(wind.gust)}${unitLowercase} gust` : ''}
              {wind.gust && formattedDirection ? ', ' : ''}
              {formattedDirection}
            </Typography>
          </Box>
        </Box>
        <Box className={styles.windMapSection}>
          <Box className={styles.windMapContainer}>
            <CurrentWindMap
              geo={location}
              isLiveAvailable={isWindLiveCurrently}
              loopAnimation={isWindLiveCurrently}
              changeAnimation={false}
              isTooltipMode
              size="large"
              windDirection={wind.direction}
              mapZoom={mapZoom}
            />
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default WindTooltip;
